import { useEffect, useRef } from 'react'
import { canonicalRegionLabel } from '@intelhub/shared/region-ontology'
import { useAuth } from '@/contexts/AuthContext'
import { useUserPreferences } from '@/hooks/queries'
import { useNotify } from '@/components/shared/useNotify'

/**
 * Plan B Plan 2 T27 — global listener that surfaces a toast whenever the API
 * sends the current user a `leak_site.burst_detected` or (KAN-801, Captain
 * Q96) a `leak_site.claim_matched` WebSocket event.
 *
 * Every leak-site toast is opt-in (Plan B §6.6, Captain Q96): the socket opens
 * only for `ransomwareRealtimeToastsEnabled`; a burst toast also needs
 * `ransomwareBurstAlertsEnabled`, exactly the server's rule (the API refuses
 * the send otherwise, so this is a UX mirror, never the control). Both kinds
 * are dormant until the server sends them (LEAK_SITE_TOASTS_MODE and the
 * dispatch secret, KAN-801).
 *
 * Claim toast copy has had the humaniser pass (KAN-801 Q96 notes).
 *
 * The component is mounted once at the top of the authenticated shell
 * (RootLayout). It is gated behind the user's `ransomwareRealtimeToastsEnabled`
 * preference (default OFF — opt-in only, per spec §6.6) so unsubscribed users
 * pay no socket cost.
 *
 * Throttle (T28): a rolling 1-hour window on the client side caps toast volume
 * at 3/hour. The user prompt + spec §6.3 want this internal to the component
 * — the API has its own per-user rate limiting on the server side, but the
 * client throttle is the safety net against burst-storms after a backfill or
 * batch ingest.
 *
 * The component renders nothing — toasts are emitted via the shared `useNotify`
 * hook which feeds the global `<Toaster />` mounted alongside it in
 * `RootLayout`.
 */

const ONE_HOUR_MS = 60 * 60 * 1000
const MAX_TOASTS_PER_HOUR = 3

interface BurstDetectedPayload {
  sector: string
  countries: string[]
  observedCount: number
  threshold: number
}

interface ClaimMatchedPayload {
  claimId: string
  groupName: string
  victimSector: string | null
  victimCountry: string | null
  claimDate: string
}

interface IncomingMessage {
  type?: string
  data?: unknown
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isClaimPayload(value: unknown): value is ClaimMatchedPayload {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    // The wire contract (leakSiteClaimMatchedPayloadSchema) is z.string().uuid().
    typeof v.claimId === 'string' &&
    UUID_RE.test(v.claimId) &&
    typeof v.groupName === 'string' &&
    v.groupName.trim().length > 0 &&
    (v.victimSector === null || typeof v.victimSector === 'string') &&
    (v.victimCountry === null || (typeof v.victimCountry === 'string' && /^[A-Za-z]{2}$/.test(v.victimCountry))) &&
    typeof v.claimDate === 'string' &&
    !Number.isNaN(Date.parse(v.claimDate))
  )
}

// "d MMM yyyy" in UTC, built by hand: ICU renders September as "Sept" in
// some locales/versions, and the copy must not drift with the runtime.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function claimDateLabel(iso: string): string {
  const d = new Date(iso)
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/** Humanised claim copy (KAN-801 Q96 notes): a claim, never a confirmed breach. */
export function buildClaimToastBody(p: ClaimMatchedPayload): { title: string; description: string } {
  const sector = p.victimSector ? p.victimSector.replace(/_/g, ' ') : null
  const country = p.victimCountry ? countryName(p.victimCountry) : null
  const date = claimDateLabel(p.claimDate)
  const where = sector && country ? `${sector} (${country})` : (sector ?? country)
  const what = where ? `posted a claim against an organisation in ${where}` : 'posted a new claim'
  return {
    title: 'New leak-site claim',
    description: `${p.groupName} ${what} on ${date}. The claim is unverified.`,
  }
}

function isBurstPayload(value: unknown): value is BurstDetectedPayload {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.sector === 'string' &&
    Array.isArray(v.countries) &&
    typeof v.observedCount === 'number' &&
    typeof v.threshold === 'number'
  )
}

function buildToastBody(payload: BurstDetectedPayload): {
  title: string
  description: string
} {
  const regions = payload.countries
    .map((region) => canonicalRegionLabel(region) ?? region)
    .join(', ')
  return {
    title: 'Ransomware burst detected',
    description: `${payload.observedCount} claims in ${payload.sector} (${regions}) in the last 24h — above your threshold of ${payload.threshold}.`,
  }
}

export function RansomwareToastListener() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const { notify } = useNotify()

  // Rolling-window timestamps of recent toasts. `useRef` keeps the buffer
  // stable across renders without re-subscribing the WS handler.
  const recentToasts = useRef<number[]>([])

  // Every leak-site toast is opt-in: no realtime opt-in, no socket at all.
  const enabled = Boolean(user) && preferences?.ransomwareRealtimeToastsEnabled === true
  // A burst toast also needs the burst preference (the server's rule).
  const burstEnabled = preferences?.ransomwareBurstAlertsEnabled === true

  useEffect(() => {
    if (!enabled) return
    if (typeof window === 'undefined') return

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = `${proto}//${window.location.host}/api/ws`
    const ws = new WebSocket(url)

    ws.onmessage = (ev) => {
      let msg: IncomingMessage
      try {
        msg = JSON.parse(ev.data) as IncomingMessage
      } catch {
        // Non-JSON frames are ignored — same defensive posture as
        // useStorySubscription.
        return
      }

      let body: { title: string; description: string }
      if (msg.type === 'leak_site.burst_detected') {
        if (!burstEnabled || !isBurstPayload(msg.data)) return
        body = buildToastBody(msg.data)
      } else if (msg.type === 'leak_site.claim_matched') {
        if (!isClaimPayload(msg.data)) return
        body = buildClaimToastBody(msg.data)
      } else {
        return
      }

      const now = Date.now()
      const cutoff = now - ONE_HOUR_MS
      // Drop expired entries before checking the cap.
      recentToasts.current = recentToasts.current.filter((t) => t > cutoff)
      if (recentToasts.current.length >= MAX_TOASTS_PER_HOUR) return

      recentToasts.current.push(now)
      notify({
        title: body.title,
        description: body.description,
        variant: 'warning',
        duration: 8000,
      })
    }

    return () => {
      ws.close()
    }
  }, [enabled, burstEnabled, notify])

  return null
}
